import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Buildings, ArrowRight, ArrowClockwise, MagnifyingGlass, ShieldCheck, Warning, Clock } from "@phosphor-icons/react";
import api, { scoreColor } from "../lib/api";
import { isApprovedPublicOperator } from "../lib/publicOperators";
import SEO from "../components/SEO";

const STATUS_LABEL = {
  verified: "Reviewed",
  partial: "Partial review",
  seed_candidate: "Seed candidate",
  archived: "Archived",
};
const TYPE_LABEL = {
  operator: "Operator",
  directory: "Directory",
  affiliate_site: "Affiliate site",
  review_site: "Review site",
  platform: "Platform",
  unknown: "Unknown",
};
function relativeFromIso(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "moments ago";
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return d === 1 ? "yesterday" : `${d} days ago`;
  const w = Math.floor(d / 7);
  if (w < 8) return `${w} weeks ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} months ago`;
  return `${Math.floor(d / 365)}y ago`;
}

function verifiedPill(op) {
  const status = op.operator_verification_status || "seed_candidate";
  if (op.coverage_status) {
    return { label: op.coverage_status, tone: "emerald" };
  }
  if (status === "verified" && op.last_verified_at) {
    const rel = relativeFromIso(op.last_verified_at);
    return rel ? { label: `Verified ${rel}`, tone: "emerald" } : null;
  }
  // No supplementary pill for verified-without-date or archived — the status
  // badge already says what we know.
  if (status === "partial") {
    return { label: "Partial review", tone: "amber" };
  }
  if (status === "seed_candidate") {
    return { label: "Seed candidate", tone: "slate" };
  }
  return null;
}

export default function OperatorsList() {
  const [ops, setOps] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [loadState, setLoadState] = useState("loading");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoadState("loading");
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.verification_status = status;
    if (type !== "all") params.operator_type = type;
    api.get("/operators", { params, signal: controller.signal })
      .then((r) => {
        if (!active) return;
        if (!Array.isArray(r.data)) throw new Error("Invalid operator directory");
        setOps(r.data);
        setLoadState("ready");
      })
      .catch(() => {
        if (active) setLoadState("error");
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [q, status, type, retry]);

  const approvedOps = useMemo(
    () => ops.filter((op) => isApprovedPublicOperator(op)),
    [ops]
  );
  const counts = useMemo(() => {
    const c = { verified: 0, partial: 0, seed_candidate: 0, archived: 0 };
    approvedOps.forEach((o) => { c[o.operator_verification_status || "seed_candidate"] = (c[o.operator_verification_status || "seed_candidate"] || 0) + 1; });
    return c;
  }, [approvedOps]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <SEO
        title="UK competition operators"
        description="Independent transparency scores for UK prize competition operators. Compare winner visibility, terms clarity, free-entry clarity and extension policy at a glance."
        path="/operators"
        ogType="website"
      />
      <div className="mb-10">
        <div className="overline text-[var(--cc-gold)] mb-2">Operators</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">UK competition operators we track</h1>
        <p className="text-[var(--cc-text-muted)] mt-3 max-w-2xl">
          Transparency scores reflect winner visibility, terms clarity,
          free-entry clarity and extension policy. Coverage labels describe
          Competition Checker's public data pipeline, not a guarantee about the
          operator.
        </p>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 mb-6 grid md:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div className="flex items-center bg-white/5 border border-white/15 rounded px-3 focus-within:border-[var(--cc-gold)]">
          <MagnifyingGlass size={16} className="text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search operators..."
            data-testid="operator-search"
            className="flex-1 bg-transparent text-white text-sm py-2.5 px-2 outline-none"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="operator-status-filter"
          className="bg-white/5 border border-white/15 rounded px-3 py-2.5 text-sm text-white"
        >
          <option value="all" className="bg-[var(--cc-navy-2)] text-white">All statuses</option>
          <option value="verified" className="bg-[var(--cc-navy-2)] text-white">Reviewed ({counts.verified || 0})</option>
          <option value="partial" className="bg-[var(--cc-navy-2)] text-white">Partial review ({counts.partial || 0})</option>
          <option value="seed_candidate" className="bg-[var(--cc-navy-2)] text-white">Seed candidate ({counts.seed_candidate || 0})</option>
          <option value="archived" className="bg-[var(--cc-navy-2)] text-white">Archived ({counts.archived || 0})</option>
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="operator-type-filter"
          className="bg-white/5 border border-white/15 rounded px-3 py-2.5 text-sm text-white"
        >
          <option value="all" className="bg-[var(--cc-navy-2)] text-white">All types</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v} className="bg-[var(--cc-navy-2)] text-white">{l}</option>)}
        </select>
      </div>

      {loadState === "loading" && (
        <p role="status" className="text-sm text-[var(--cc-text-muted)] mb-4">
          {approvedOps.length ? "Updating operators..." : "Loading operators..."}
        </p>
      )}
      {loadState === "error" && (
        <div role="alert" className="text-sm text-[var(--cc-text-muted)] mb-4">
          <p>We could not load the operator directory. Please try again.</p>
          {approvedOps.length > 0 && <p>Previously loaded operators are shown below.</p>}
          <button type="button" onClick={() => setRetry((value) => value + 1)} className="inline-flex items-center gap-2 text-[var(--cc-gold)] py-3">
            <ArrowClockwise size={16} aria-hidden="true" /> Try again
          </button>
        </div>
      )}
      {loadState === "ready" && <div className="text-xs text-[var(--cc-text-muted)] mb-4">{approvedOps.length} operators</div>}

      {approvedOps.length === 0 ? loadState === "ready" && (
        <div className="border border-dashed border-white/15 rounded-lg p-12 text-center text-[var(--cc-text-muted)]">
          No operators match those filters.
        </div>
      ) : (
        <div className="space-y-10">
          {approvedOps.length > 0 && (
            <section data-testid="operators-live-section">
              <div className="overline text-[var(--cc-gold)] mb-3">Live coverage</div>
              <div className="grid gap-4 md:grid-cols-2">
                {approvedOps.map((op) => <LiveOperatorCard key={op.id} op={op} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

const LiveOperatorCard = ({ op }) => {
  const pill = verifiedPill(op);
  const noComps = (op.active_competition_count || 0) === 0;
  return (
    <Link
      to={`/operators/${op.slug}`}
      data-testid={`operator-card-${op.slug}`}
      className="comp-card flex flex-col p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 overline text-[var(--cc-text-dark-muted)] mb-1">
            <Buildings size={12} weight="fill" /> {op.active_competition_count} active
          </div>
          <h2 className="font-display text-xl font-semibold text-[var(--cc-text-dark)]">{op.name}</h2>
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px]">
            <StatusBadge status={op.operator_verification_status} />
            {op.operator_type && op.operator_type !== "operator" && (
              <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-semibold uppercase tracking-wider">
                {TYPE_LABEL[op.operator_type] || op.operator_type}
              </span>
            )}
            {op.priority_tier === "primary" && (
              <span className="px-2 py-0.5 rounded-full bg-[var(--cc-navy)] text-[var(--cc-gold)] font-semibold uppercase tracking-wider">
                Primary
              </span>
            )}
            {op.coverage_type && (
              <span
                data-testid={`operator-coverage-type-${op.slug}`}
                className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold uppercase tracking-wider"
              >
                {op.coverage_type}
              </span>
            )}
            {pill && (
              <span
                data-testid={`operator-coverage-status-${op.slug}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider border ${
                  pill.tone === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                  pill.tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200" :
                  "bg-slate-100 text-slate-700 border-slate-300"
                }`}
              >
                <Clock size={9} weight="fill" /> {pill.label}
              </span>
            )}
          </div>
        </div>
        <div
          className="h-14 w-14 rounded-full flex items-center justify-center font-mono-num font-bold border-2 text-[var(--cc-text-dark)] flex-none"
          style={{ borderColor: scoreColor(op.trust_score) }}
        >
          {op.trust_score}
        </div>
      </div>
      <p className="text-sm text-[var(--cc-text-dark-muted)] mt-3 leading-relaxed line-clamp-2">{op.trust_summary}</p>
      {op.coverage_note && (
        <p
          data-testid={`operator-coverage-note-${op.slug}`}
          className="text-xs text-slate-600 mt-2 leading-relaxed"
        >
          {op.coverage_note}
        </p>
      )}
      {noComps && (
        <div
          data-testid={`operator-no-comps-${op.slug}`}
          className="mt-3 rounded-md bg-slate-100 border border-slate-200 px-3 py-2 text-[11px] text-slate-700 leading-relaxed"
        >
          <span className="font-semibold">Live coverage configured.</span> No public competitions are currently visible.
        </div>
      )}
      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        <SmallScore label="Winners" v={op.winner_visibility_score} />
        <SmallScore label="Terms" v={op.terms_clarity_score} />
        <SmallScore label="Free entry" v={op.free_entry_clarity_score} />
        <SmallScore label="Extension" v={op.extension_policy_clarity_score} />
      </div>
      <div className="mt-5 inline-flex items-center text-sm text-[var(--cc-bronze)] font-semibold">
        View operator <ArrowRight size={14} className="ml-1" />
      </div>
    </Link>
  );
};

const StatusBadge = ({ status }) => {
  const s = status || "seed_candidate";
  const styles = {
    verified: "bg-emerald-100 text-emerald-800 border-emerald-200",
    partial: "bg-amber-100 text-amber-800 border-amber-200",
    seed_candidate: "bg-slate-200 text-slate-700 border-slate-300",
    archived: "bg-slate-300 text-slate-600 border-slate-400",
  }[s];
  const Icon = s === "verified" ? ShieldCheck : s === "archived" ? null : Warning;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${styles}`}>
      {Icon && <Icon size={10} weight="fill" />}
      {STATUS_LABEL[s]}
    </span>
  );
};

const SmallScore = ({ label, v }) => (
  <div className="rounded bg-slate-100 px-2 py-2">
    <div className="overline text-[10px] text-[var(--cc-text-dark-muted)]">{label}</div>
    <div className="font-mono-num text-sm font-semibold" style={{ color: scoreColor(v) }}>{v}</div>
  </div>
);
